import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, FileText, RotateCcw } from "lucide-react";

const SELECTS = [
  { key: "desired_timeline", label: "When would you like a first paying test?", options: [["1_month", "Within a month"], ["3_months", "Within 3 months"], ["6_months", "Within 6 months"]] },
  { key: "capital_comfort", label: "Money you're comfortable putting in", options: [["none", "Nothing upfront"], ["low", "A small amount"], ["moderate", "A moderate amount"]] },
  { key: "selling_comfort", label: "How comfortable are you selling?", options: [["low", "Not comfortable yet"], ["medium", "Somewhat comfortable"], ["high", "Comfortable"]] },
  { key: "visibility_preference", label: "Public visibility you're willing to have", options: [["private", "Keep it private"], ["selective", "Selective / 1-to-1 outreach"], ["public", "Happy to post publicly"]] },
  { key: "moonlighting_restriction", label: "Does your employment restrict outside work?", options: [["no", "No restriction"], ["unsure", "I'm not sure"], ["yes", "Yes, there are restrictions"]] },
];

const DELIVERY = [["one_to_one", "One-to-one"], ["group", "Group"], ["async", "Async / written"], ["project", "Project"], ["retainer", "Retainer"], ["productised", "Productised"], ["teaching", "Teaching"]];

const field = "w-full mt-1.5 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-base sm:text-sm text-slate-900 focus:border-sky-200 focus:outline-none";

export const StepProfile = ({ state, onSaved }) => {
  const existing = state.profile || {};
  const [form, setForm] = useState({
    resume_id: existing.resume_id || state.resumes?.[0]?.resume_id || "",
    hours_per_week: existing.hours_per_week || 5,
    desired_income: existing.desired_income || 10000,
    desired_timeline: existing.desired_timeline || "3_months",
    capital_comfort: existing.capital_comfort || "none",
    selling_comfort: existing.selling_comfort || "medium",
    visibility_preference: existing.visibility_preference || "selective",
    moonlighting_restriction: existing.moonlighting_restriction || "unsure",
    delivery_preference: existing.delivery_preference || ["one_to_one"],
  });
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const restart = async () => {
    if (!window.confirm("Start this run again from scratch? Your capabilities, routes and blueprint for this resume will be rebuilt. Anything you already handed off to validation stays in your history.")) return;
    setRestarting(true);
    try {
      await api.post("/accelerator/restart");
      toast.success("Cleared — confirm your profile below to start again");
      onSaved({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restart this run");
    } finally { setRestarting(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDelivery = (value) =>
    set("delivery_preference", form.delivery_preference.includes(value)
      ? form.delivery_preference.filter((v) => v !== value)
      : [...form.delivery_preference, value]);

  const switching = !!(existing.resume_id && form.resume_id && existing.resume_id !== form.resume_id);

  const submit = async () => {
    if (!form.resume_id) return toast.error("Analyse a resume on your dashboard first.");
    if (!form.delivery_preference.length) return toast.error("Pick at least one way you'd like to deliver.");
    setSaving(true);
    try {
      const { data } = await api.post("/accelerator/profile", form);
      toast.success(data.resume_switched
        ? "New resume selected — we start this run fresh from it, and your earlier run stays in your history"
        : data.resume_changed ? "Profile updated from your newer resume — review your blueprint" : "Profile confirmed");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your profile");
    } finally {
      setSaving(false);
    }
  };

  if (!state.resumes?.length) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-700" data-testid="accelerator-needs-resume">
        Upload and analyse your resume on your Career Intelligence dashboard first — the Accelerator only works from what your profile actually shows.
      </div>
    );
  }

  return (
    <section className="space-y-4" data-testid="accelerator-step-profile-panel">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base md:text-lg font-bold text-slate-900">Step 1 — Confirm what we already know</h2>
            <p className="text-sm text-slate-500 mt-1.5">We use your analysed resume plus a few constraints. Nothing here is invented, and nothing is shared with other users.</p>
          </div>
          {state.can_redo && state.profile && (
            <Button onClick={restart} disabled={restarting || saving} variant="outline"
              className="h-9 shrink-0 rounded-xl border-slate-300 text-xs text-slate-600" data-testid="accelerator-restart">
              {restarting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />} Start this run again
            </Button>
          )}
        </div>

        <label className="block mt-5 text-xs text-slate-500">Which resume should we read?
          <select value={form.resume_id} onChange={(e) => set("resume_id", e.target.value)} className={field} data-testid="accelerator-resume-select">
            {state.resumes.map((r) => (
              <option key={r.resume_id} value={r.resume_id}>{r.filename} · {new Date(r.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</option>
            ))}
          </select>
        </label>

        {switching && (
          <p className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-slate-700" data-testid="accelerator-resume-switch-note">
            This is a different resume to the one your current run was built on. Confirming starts a fresh run read only
            from this resume — your earlier capabilities, routes and blueprint stay saved in your history, untouched.
          </p>
        )}

        {state.profile?.current_role && !switching && (
          <p className="mt-3 inline-flex items-start gap-2 text-xs text-slate-500" data-testid="accelerator-profile-source">
            <FileText className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
            From your resume: {state.profile.current_role}{state.profile.years_experience ? ` · ${state.profile.years_experience} yrs` : ""}{state.profile.industries?.length ? ` · ${state.profile.industries.slice(0, 2).join(", ")}` : ""}
          </p>
        )}

        <div className="grid gap-4 mt-5 sm:grid-cols-2">
          <label className="block text-xs text-slate-500">Hours a week you can genuinely give
            <input type="number" min={1} max={40} value={form.hours_per_week} onChange={(e) => set("hours_per_week", Number(e.target.value))} className={field} data-testid="accelerator-hours-input" />
          </label>
          <label className="block text-xs text-slate-500">Target additional income (₹ per month)
            <input type="number" min={1000} step={500} value={form.desired_income} onChange={(e) => set("desired_income", Number(e.target.value))} className={field} data-testid="accelerator-income-input" />
          </label>
          {SELECTS.map((s) => (
            <label key={s.key} className="block text-xs text-slate-500">{s.label}
              <select value={form[s.key]} onChange={(e) => set(s.key, e.target.value)} className={field} data-testid={`accelerator-${s.key}-select`}>
                {s.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          ))}
        </div>

        <p className="mt-5 text-xs text-slate-500">How would you be happy to deliver?</p>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="accelerator-delivery-options">
          {DELIVERY.map(([v, l]) => (
            <button key={v} onClick={() => toggleDelivery(v)} data-testid={`accelerator-delivery-${v}`}
              className={`rounded-full border px-3 py-2 text-xs transition-colors ${form.delivery_preference.includes(v) ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-500 hover:border-slate-300"}`}>
              {l}
            </button>
          ))}
        </div>

        {form.moonlighting_restriction !== "no" && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="accelerator-moonlighting-note">
            Check your employment agreement and applicable policies before pursuing this commercially. We can't confirm legal clearance for you.
          </p>
        )}

        <Button onClick={submit} disabled={saving} className="mt-6 h-11 w-full sm:w-auto bg-sky-600 hover:bg-sky-500 text-white" data-testid="accelerator-save-profile">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Confirm profile & continue"}
        </Button>
      </div>
    </section>
  );
};

export default StepProfile;
